import { buildCreateBody, toYmd } from '../../Components/KnowledgeLibrary/knowledgeLibraryConfig';

/**
 * Helpers for the Trinity Knowledge page: the environment a build targets, the defaults for a
 * drop-in, the create bodies for drop-ins, new law versions and amendments, and the family and
 * freshness views.
 */

/**
 * The environment this Trinity build writes to, from its IAM API host (.env.trinity.dev, .prod,
 * .prod-au). A .au host is prod-au, a host whose first label has "dev" is dev, any other
 * zavior.app host is prod. Returns null when the build has no API host, so nothing can be indexed.
 */
export function environmentFromApi(baseUrl) {
  let host = '';
  try {
    host = new URL(baseUrl).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (!host) return null;
  if (host.endsWith('.au')) return { key: 'prod-au', host };
  if (host.split('.')[0].includes('dev')) return { key: 'dev', host };
  if (host.endsWith('zavior.app')) return { key: 'prod', host };
  return { key: host, host };
}

/** Details every drop-in gets unless the Details section changes them. */
export const DROP_DEFAULTS = {
  title: '',
  short_name: '',
  industries: [], // none: every expert can find it
  authority_tier: 'L2',
  doc_type: 'guidance',
  jurisdiction: 'INTL',
  licence_mode: 'cite_only',
  as_of: '',
  source_url: '',
  frameworks: '',
  sectors: '',
};

/** The file name without its extension, as a title. */
export const titleFromFile = (name) => String(name || '').replace(/\.[^.]+$/, '').trim() || 'Untitled document';

const shortId = () => Math.random().toString(36).slice(2, 8).padEnd(6, '0');

/** A library_key from the title plus a short id, so nobody types one: "pdpa-guide-k3x9q2". */
export function libraryKeyFor(title, id = shortId()) {
  const slug = String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100)
    .replace(/-+$/, '');
  return `${slug || 'item'}-${id}`;
}

/**
 * The POST /knowledge-library body for one dropped file: an upload row, Published, so IAM stamps
 * the signed-in Tech Admin as reviewer. A title in Details applies only when one file is dropped.
 * extra adds version or amendment fields.
 */
export function dropBody({ fileName, fileId, details = DROP_DEFAULTS, single = true, extra = {}, keyId }) {
  const title = (single && String(details.title || '').trim()) || titleFromFile(fileName);
  const body = buildCreateBody({
    library_key: libraryKeyFor(title, keyId),
    title,
    doc_type: details.doc_type || DROP_DEFAULTS.doc_type,
    authority_tier: details.authority_tier || DROP_DEFAULTS.authority_tier,
    jurisdiction: details.jurisdiction || DROP_DEFAULTS.jurisdiction,
    licence_mode: details.licence_mode || DROP_DEFAULTS.licence_mode,
    status: 'Published',
    as_of: details.as_of || '',
    source_url: details.source_url || '',
    file_id: fileId,
    industries: details.industries || [],
    frameworks: details.frameworks || '',
    sectors: details.sectors || '',
  });
  const shortName = String(details.short_name || '').trim();
  if (shortName) body.short_name = shortName;
  return { ...body, ...extra };
}

const today = () => toYmd(new Date());

/** In force from a date on or before today; otherwise waiting to commence. */
const statusFor = (date) => (date && date > today() ? 'pending_commencement' : 'in_force');

/** Details for a new version of a law: same family, title, tier and industries as the current one. */
export function newVersionDetails(current) {
  return {
    ...DROP_DEFAULTS,
    title: current.title || '',
    short_name: current.short_name || '',
    industries: current.industries || [],
    authority_tier: current.authority_tier || 'L1',
    doc_type: current.doc_type || 'law',
    jurisdiction: current.jurisdiction || DROP_DEFAULTS.jurisdiction,
    licence_mode: current.licence_mode || DROP_DEFAULTS.licence_mode,
    source_url: current.source_url || '',
    frameworks: (current.frameworks || []).join(', '),
    sectors: (current.sectors || []).join(', '),
  };
}

/** Details for an amending Act: a law (L1) in the same jurisdiction and industries; its own title. */
export function amendmentDetails(parent) {
  return {
    ...DROP_DEFAULTS,
    industries: parent.industries || [],
    authority_tier: 'L1',
    doc_type: 'law',
    jurisdiction: parent.jurisdiction || DROP_DEFAULTS.jurisdiction,
    licence_mode: parent.licence_mode || DROP_DEFAULTS.licence_mode,
  };
}

/** A law's family: family_key, which IAM defaults to the library_key. */
export const familyOf = (row) => row?.family_key || row?.library_key || '';

/** The fields a new version adds to its body; version_label and effective_from are required here. */
export const versionExtra = (current, { versionLabel, effectiveFrom }) => ({
  family_key: familyOf(current),
  version_label: String(versionLabel || '').trim(),
  effective_from: effectiveFrom,
  legal_status: statusFor(effectiveFrom),
});

/** The fields an amendment adds; commencement_date is required here. */
export const amendmentExtra = (parent, { commencementDate }) => ({
  amends_family_key: familyOf(parent),
  commencement_date: commencementDate,
  legal_status: statusFor(commencementDate),
});

/** The rows of a family response: versions (newest effective_from first), then amendments. */
export function splitFamily(rows, familyKey) {
  const list = Array.isArray(rows) ? rows : [];
  const byDate = (field) => (a, b) => String(b[field] || '').localeCompare(String(a[field] || ''));
  return {
    versions: list.filter((r) => r.amends_family_key !== familyKey).sort(byDate('effective_from')),
    amendments: list.filter((r) => r.amends_family_key === familyKey).sort(byDate('commencement_date')),
  };
}

/** The in-force version of a family: legal_status in_force, else the live one with no effective_to. */
export function inForceVersion(versions) {
  return versions.find((v) => v.legal_status === 'in_force')
    || versions.find((v) => !v.effective_to && v.legal_status !== 'repealed' && v.legal_status !== 'superseded')
    || null;
}

/** Laws whose review_due date has passed, oldest first. */
export function overdueLaws(rows, now = today()) {
  return (rows || [])
    .filter((r) => r.doc_type === 'law' && r.review_due && String(r.review_due).slice(0, 10) < now && !r.is_deleted)
    .sort((a, b) => String(a.review_due).localeCompare(String(b.review_due)));
}
